import { create } from 'zustand';
import { api, setToken, getToken, ApiError } from '@/lib/api';

interface AdminMe {
  id: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  isAdmin: boolean;
}

interface AuthState {
  token: string | null;
  me: AdminMe | null;
  status: 'idle' | 'loading' | 'ready' | 'error';
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  bootstrap: () => Promise<void>;
}

export const useAuth = create<AuthState>((set) => ({
  token: getToken(),
  me: null,
  status: 'idle',

  bootstrap: async () => {
    const token = getToken();
    if (!token) { set({ status: 'idle' }); return; }
    set({ status: 'loading' });
    try {
      const res = await api.get<{ data: AdminMe }>('/api/admin/me');
      set({ me: res.data, status: 'ready' });
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        setToken(null);
        set({ token: null, me: null, status: 'idle' });
      } else {
        set({ status: 'error' });
      }
    }
  },

  login: async (email, password) => {
    set({ status: 'loading' });
    try {
      const res = await api.post<{ data: { accessToken: string } }>(
        '/api/auth/email/login', { email, password },
      );
      setToken(res.data.accessToken);
      set({ token: res.data.accessToken });
      const me = await api.get<{ data: AdminMe }>('/api/admin/me');
      set({ me: me.data, status: 'ready' });
    } catch (err) {
      setToken(null);
      set({ token: null, me: null, status: 'idle' });
      throw err;
    }
  },

  logout: () => {
    setToken(null);
    set({ token: null, me: null, status: 'idle' });
  },
}));
